import { BookingOnboardingDto } from '@shared/core/booking-onboarding'
import { GetProfileResponseDto } from '@shared/core/users'
import { Clock4, CodeXml, Copy, ExternalLink, Rows3 } from 'lucide-react'
import { FC, useState } from 'react'
import { Button, Typography } from 'src/modules/core/components'
import { AddToWebsiteModal } from './add-to-website-modal'
import { BookingBrandingCard } from './booking-branding-card'
import classes from './settings-booking-page-tab.module.scss'

interface BookingPageDetailsProps {
  bookingOnboarding: BookingOnboardingDto
  profile: GetProfileResponseDto
  onShowAvailabilitySettings: () => void
  onShowAppointmentTypesSettings: () => void
}

export const BookingPageDetails: FC<BookingPageDetailsProps> = ({
  bookingOnboarding,
  profile,
  onShowAvailabilitySettings,
  onShowAppointmentTypesSettings,
}) => {
  const [copied, setCopied] = useState(false)
  const [addToWebsiteModalOpen, setAddToWebsiteModalOpen] = useState(false)

  const bookingPageUrl = bookingOnboarding?.booking_page_path
    ? `${window.location.origin}/book/${bookingOnboarding.booking_page_path}`
    : ''

  const handleCopyUrl = async () => {
    if (bookingPageUrl) {
      try {
        await navigator.clipboard.writeText(bookingPageUrl)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } catch (err) {
        console.error('Failed to copy URL:', err)
      }
    }
  }

  return (
    <div className={classes.onboardingSection}>
      <div className={classes.section}>
        <BookingBrandingCard
          bookingOnboarding={bookingOnboarding}
          bookingPageUrl={bookingPageUrl}
          accountName={profile.full_name ?? ''}
        />
      </div>

      <div className={classes.section}>
        <div className={classes.sectionCard}>
          <Typography variant="h5" className={classes.sectionTitle}>
            Share your booking page
          </Typography>
          <Typography variant="body" className={classes.sectionDescription}>
            Share this link with clients or embed it on your website.
          </Typography>
          <div className={classes.urlBlock}>
            <div className={classes.urlRow}>
              <Typography variant="body" className={classes.urlText}>
                {bookingPageUrl}
              </Typography>
              <a href={bookingPageUrl} target="_blank" rel="noopener noreferrer" className={classes.viewPageLink}>
                <ExternalLink className={classes.viewPageLinkIcon} />
                View page
              </a>
            </div>
            <div className={classes.actionLinks}>
              <button type="button" className={classes.actionLink} onClick={handleCopyUrl}>
                <Copy className={classes.actionLinkIcon} />
                {copied ? 'Copied!' : 'Copy booking link'}
              </button>
              <button type="button" className={classes.actionLink} onClick={() => setAddToWebsiteModalOpen(true)}>
                <CodeXml className={classes.actionLinkIcon} />
                Add to your website
              </button>
            </div>
          </div>
          {addToWebsiteModalOpen && (
            <AddToWebsiteModal bookingPageUrl={bookingPageUrl} onClose={() => setAddToWebsiteModalOpen(false)} />
          )}
          <Typography variant="body" className={classes.helpText}>
            We&apos;ll automatically add appointments to your calendars when customers book with you, and set reminders
            using the phone number they provide.
          </Typography>
        </div>
      </div>

      <div className={classes.section}>
        <div className={classes.sectionCard}>
          <Typography variant="h5" className={classes.sectionTitle}>
            Manage your booking page
          </Typography>
          <Typography variant="body" className={classes.sectionDescription}>
            Set your availability and define the types of appointments you offer.
          </Typography>
          <div className={classes.manageButtons}>
            <Button variant="withBorder" onClick={onShowAvailabilitySettings} className={classes.manageButton}>
              <Clock4 />
              Manage availability
            </Button>
            <Button variant="withBorder" onClick={onShowAppointmentTypesSettings} className={classes.manageButton}>
              <Rows3 />
              Manage appointment types
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
